import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Play, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";

const HERO_TRACK = {
    id: "eYq7WapuDLU",
    title: "Enjoy Enjaami",
    artist: "Dhee ft. Arivu · prod. Santhosh Narayanan",
    views: "508M+",
    date: "March 2021",
    badge: "The track that started a movement",
    tag: "Cultural phenomenon",
};

const TRACKS = [
    {
        id: "e_0Q92a8Wlo",
        title: "Neeye Oli",
        artist: "SVDP, Navz-47, Santhosh Narayanan",
        date: "Jul 2021",
        tag: "Sarpatta Parambarai",
    },
    {
        id: "SHeSdqHRMCo",
        title: "Made in Jaffna",
        artist: "Shan Vincent de Paul",
        date: "Sep 2021",
        tag: "Album title track",
    },
    {
        id: "KfEtoLSDh9A",
        title: "Savage",
        artist: "SVDP ft. Usha Jey",
        date: "Jun 2021",
        tag: "Toronto · Tamil fusion",
    },
    {
        id: "aU9mePHp2ZE",
        title: "Thooki Veesu",
        artist: "Shaq-T & Dasa ft. King Sinister",
        views: "72K",
        date: "Aug 2023",
        tag: "Tamil hip-hop",
    },
    {
        id: "JeIdlyJA6Oc",
        title: "The Dance Song · Mami",
        artist: "Yung Raja",
        views: "42K",
        date: "YAALL FEST 2022",
        tag: "Live performance",
    },
    {
        id: "A3DEqTwyO-g",
        title: "Kaadhal Aagalaam",
        artist: "Magisha",
        views: "32K",
        date: "YAALL FEST 2022",
        tag: "Live performance",
    },
    {
        id: "BbAM77SaYfA",
        title: "Ondrallo",
        artist: "Tha Mystro",
        views: "20K",
        date: "YAALL FEST 2022",
        tag: "Live performance",
    },
    {
        id: "kHsbSo0I5R0",
        title: "Throw It (ft. DHP)",
        artist: "Shaq-T & Dasa",
        views: "12K",
        date: "Aug 2023",
        tag: "Tamil hip-hop",
    },
];

const thumb = (id) => `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
const fallback = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
const watch = (id) => `https://www.youtube.com/watch?v=${id}`;

export const MaajjaSound = () => {
    const scrollerRef = useRef(null);

    const scrollBy = (dir) => {
        if (!scrollerRef.current) return;
        const w = scrollerRef.current.clientWidth;
        scrollerRef.current.scrollBy({ left: dir * w * 0.85, behavior: "smooth" });
    };

    return (
        <section
            id="sounds"
            className="relative py-28 lg:py-36 overflow-hidden"
            data-testid="maajja-sound-section"
        >
            <div className="absolute -top-32 right-1/4 w-[500px] h-[500px] rounded-full bg-[#F54815] opacity-10 blur-[160px] pointer-events-none" />
            <div className="absolute bottom-20 -left-32 w-[500px] h-[500px] rounded-full bg-[#791EE2] opacity-15 blur-[160px] pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 relative">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                    className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 lg:mb-16"
                >
                    <div className="max-w-3xl">
                        <p className="text-sm uppercase tracking-[0.3em] text-[#F54815] mb-4 font-medium">
                            The sound of Maajja
                        </p>
                        <h2
                            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]"
                            data-testid="sound-heading"
                        >
                            Independent music
                            <br />
                            that went <span className="text-[#791EE2]">global</span>.
                        </h2>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => scrollBy(-1)}
                            className="w-12 h-12 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 flex items-center justify-center text-white transition-all active:scale-95"
                            aria-label="Scroll left"
                            data-testid="sound-scroll-left"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            onClick={() => scrollBy(1)}
                            className="w-12 h-12 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 flex items-center justify-center text-white transition-all active:scale-95"
                            aria-label="Scroll right"
                            data-testid="sound-scroll-right"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </motion.div>

                {/* Hero featured track */}
                <motion.a
                    href={watch(HERO_TRACK.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.8 }}
                    className="group relative block rounded-[28px] overflow-hidden border border-white/10 hover:border-[#F54815]/40 mb-10 transition-all"
                    data-testid="hero-track-card"
                >
                    <div className="grid grid-cols-1 md:grid-cols-12 min-h-[420px]">
                        {/* Thumbnail */}
                        <div className="md:col-span-7 relative aspect-video md:aspect-auto overflow-hidden">
                            <img
                                src={thumb(HERO_TRACK.id)}
                                onError={(e) => (e.currentTarget.src = fallback(HERO_TRACK.id))}
                                alt={HERO_TRACK.title}
                                className="w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-1000"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-[#030023] via-[#030023]/30 to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-[#030023]" />

                            {/* Play button */}
                            <div className="absolute inset-0 flex items-center justify-center md:justify-start md:pl-16">
                                <div className="relative">
                                    <div className="absolute inset-0 rounded-full bg-[#F54815] blur-2xl opacity-60 group-hover:opacity-100 transition" />
                                    <div className="relative w-20 h-20 lg:w-24 lg:h-24 rounded-full bg-white flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <Play
                                            size={32}
                                            className="text-[#030023] fill-[#030023] ml-1"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Info */}
                        <div className="md:col-span-5 relative p-8 md:p-12 lg:p-14 flex flex-col justify-center bg-gradient-to-br from-[#0a0436] to-[#030023]">
                            <p className="text-[11px] uppercase tracking-[0.25em] text-[#F54815] mb-4 font-semibold">
                                {HERO_TRACK.badge}
                            </p>
                            <h3 className="font-heading text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white tracking-tighter leading-[0.95] mb-5">
                                {HERO_TRACK.title}
                            </h3>
                            <p className="text-white/65 font-body mb-8 leading-relaxed">
                                {HERO_TRACK.artist}
                            </p>

                            <div className="flex flex-wrap items-center gap-6 pt-6 border-t border-white/10">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 font-medium">
                                        Views
                                    </div>
                                    <div className="font-heading text-2xl lg:text-3xl font-bold text-white">
                                        {HERO_TRACK.views}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 font-medium">
                                        Released
                                    </div>
                                    <div className="font-heading text-lg font-semibold text-white">
                                        {HERO_TRACK.date}
                                    </div>
                                </div>
                                <div className="ml-auto inline-flex items-center gap-2 text-sm text-white/70 group-hover:text-white transition">
                                    Watch <ExternalLink size={14} />
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.a>

                {/* Horizontal scroller */}
                <div
                    ref={scrollerRef}
                    className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 md:-mx-10 lg:-mx-16 px-6 md:px-10 lg:px-16 scrollbar-hide"
                    style={{ scrollbarWidth: "none" }}
                    data-testid="sound-scroller"
                >
                    {TRACKS.map((t, i) => (
                        <motion.a
                            key={t.id}
                            href={watch(t.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.5, delay: i * 0.07 }}
                            className="group snap-start flex-shrink-0 w-[300px] md:w-[340px] rounded-2xl overflow-hidden border border-white/10 hover:border-[#791EE2]/40 bg-white/[0.03] hover:bg-white/[0.06] transition-all"
                            data-testid={`track-card-${i}`}
                        >
                            <div className="relative aspect-video overflow-hidden">
                                <img
                                    src={thumb(t.id)}
                                    onError={(e) => (e.currentTarget.src = fallback(t.id))}
                                    alt={t.title}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#030023] via-transparent to-transparent opacity-80" />

                                {/* Play overlay */}
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center scale-90 group-hover:scale-100 transition-transform">
                                        <Play
                                            size={20}
                                            className="text-[#030023] fill-[#030023] ml-0.5"
                                        />
                                    </div>
                                </div>

                                {/* Tag */}
                                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-sm border border-white/10">
                                    <span className="text-[10px] uppercase tracking-[0.15em] text-white/90 font-medium">
                                        {t.tag}
                                    </span>
                                </div>

                                {/* Mini equalizer on hover */}
                                <div className="absolute bottom-3 right-3 flex items-end h-5 gap-[2px] opacity-0 group-hover:opacity-100 transition-opacity">
                                    {[8, 14, 10, 16, 12].map((h, k) => (
                                        <span
                                            key={k}
                                            className="w-[2px] rounded-sm eq-bar bg-[#F54815]"
                                            style={{
                                                height: `${h}px`,
                                                animationDelay: `${k * 0.12}s`,
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="p-5">
                                <h4 className="font-heading text-lg font-bold text-white tracking-tight leading-snug mb-2 line-clamp-2">
                                    {t.title}
                                </h4>
                                <p className="text-sm text-white/55 line-clamp-1 mb-4">
                                    {t.artist}
                                </p>
                                <div className="flex items-center justify-between pt-3 border-t border-white/10">
                                    {t.views && (
                                        <span className="text-xs text-white/60 font-medium">
                                            {t.views} views
                                        </span>
                                    )}
                                    <span className="text-xs text-white/40 ml-auto">
                                        {t.date}
                                    </span>
                                </div>
                            </div>
                        </motion.a>
                    ))}
                </div>

                {/* CTA strip */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-6 lg:p-8 rounded-2xl border border-white/10 bg-white/[0.02]"
                >
                    <div>
                        <h4 className="font-heading text-xl lg:text-2xl font-bold text-white tracking-tight mb-1">
                            500M+ streams. Zero label gatekeepers.
                        </h4>
                        <p className="text-white/55 text-sm">
                            Every track above was released independently through
                            Maajja.
                        </p>
                    </div>
                    <a
                        href="https://www.youtube.com/@maajja"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#791EE2] hover:bg-[#8b32f0] text-white px-6 py-3 rounded-full font-semibold text-sm transition-all hover:shadow-[0_0_30px_rgba(121,30,226,0.45)] active:scale-95 whitespace-nowrap"
                        data-testid="sound-channel-cta"
                    >
                        Explore the channel
                        <ExternalLink size={14} />
                    </a>
                </motion.div>
            </div>
        </section>
    );
};
